// @ts-check
/* eslint-disable max-lines */

import { evaluatedDataSelector } from "@app/data/machine/selectors";
import { findInList, formulaProcessors, isObject } from "@app/common/utils";
import { generateDataActorSelector } from "@app/data/machine/generators/generateDataActorSelector";
import { isEqual, set } from "lodash";

/**
 * Map of formula attributes by attribute id (entity.attribute)
 * Used for re-evaluating formula attributes after the formula attribute has been evaluated
 * And the formula properties are no longer available on the bobj
 * @type {{ [`entity.attribute`] : { type: string, value: string}}}
 */
export const formulaAttributeMapByAttributeId = {};

/**
 * An object that does not have any properties with values that are formula definition objects.
 * Formula definitions are objects with type and value property.
 * @typedef SweftEvaluatedObject
 * @type {Object}
 */

/**
 * Evaluates the formula attributes of the bobj, replacing the formula definition objects with the evaluated value.
 * Because the formula definitions are being replaced, a copy of the formula definition is kept in the
 * formulaAttributeMapByAttributeId map. The next time this bobj needs to be evaluated, the formula definition is accessed
 * from the map.
 * @param {Object} bobj
 * @returns {SweftEvaluatedObject}
 */
export const evaluateBobj = ({ bobj = {} } = {}) => Object.keys(bobj).reduce((evaluatedBobj, nextKey) => {
    const nextValue = bobj[nextKey];
    if (!isObject(nextValue)) {
        if (formulaAttributeMapByAttributeId[`${bobj.entity}.${nextKey}`]) {
            const { type, value } = formulaAttributeMapByAttributeId[`${bobj.entity}.${nextKey}`];
            const processedValue = formulaProcessors[type](bobj, value);
            return {
                ...evaluatedBobj,
                [nextKey]: processedValue
            };
        }
        return {
            ...evaluatedBobj,
            [nextKey]: nextValue
        };
    }
    const { type = null, value = null, ...restOfObjectValue } = nextValue;
    if (isObject(nextValue)) {
        for (const [key, getValue] of Object.entries(nextValue)) {
            // ignore they key formulaDefinition to stop superadmin formulas from getting processed
            if (getValue?.type && getValue?.value && key !== 'formulaDefinition') {
                const { type = null, value = null } = getValue;
                if (type !== null && value !== null && formulaProcessors[type]) {
                    const processedValue = formulaProcessors[type](nextValue, value);
                    nextValue[key] = processedValue;
                }
            }
        }
        evaluateBobj({ bobj: nextValue });
    }
    if (Object.keys(restOfObjectValue).length !== 0) {
        return {
            ...evaluatedBobj,
            [nextKey]: nextValue
        };
    }
    if (type !== null && value !== null && formulaProcessors[type]) {
        const processedValue = formulaProcessors[type](bobj, value);
        formulaAttributeMapByAttributeId[`${bobj.entity}.${nextKey}`] = { type, value };
        return {
            ...evaluatedBobj,
            [nextKey]: processedValue
        };
    }
    return {
        ...evaluatedBobj,
        [nextKey]: null
    };
}, {});

export const evaluateBobjList = ({ bobjList = [] } = {}) => {
    if (Array.isArray(bobjList)) {
        return bobjList.map((bobj) => evaluateBobj({ bobj }));
    }
    return [];
};

export const evaluateBobjListWithFormulaList = ({ bobjList = [], formulaList = [], dataMappingModuleConfig = {} } = {}) => {
    return bobjList?.length > 0 && bobjList.map((bobj) => {
        if (dataMappingModuleConfig?.enabled && isObject(bobj)) {
            bobj = evaluateBobj({ bobj });
        }
        return evaluateSingleBobjWithFormulaList({ bobj, formulaList });
    });
};

export const evaluateSingleBobjWithFormulaList = ({ bobj = {}, formulaList = [] } = {}) => {
    return formulaList?.reduce((evaluatedBobj, nextFormulaObj) => {
        const evaluatedValue = nextFormulaObj?.type ? formulaProcessors[nextFormulaObj?.type](evaluatedBobj, nextFormulaObj.value) : {};
        return set(evaluatedBobj, nextFormulaObj.path, evaluatedValue);
    }, bobj);
};

/**
 * Returns the data for the provided in entity that is in the entity data actor of the provided in interpreted machine
 * @type {Object}
 * @param {SweftDataMachineService} machineService
 * @param {string} entity
 * @returns {[]|*}
 */
export const getActorDataFromMachineService = ({ machineService, entity, dataActorId }) => {
    const dataActorSelector = generateDataActorSelector({ dataActorOptions: { dataActorType: "entity", entity }, dataActorId });
    const dataActor = dataActorSelector(machineService.getSnapshot());
    return evaluatedDataSelector(dataActor.ref.getSnapshot());
};

export const getActorRefFromMachineService = ({ machineService, entity, dataActorId }) => {
    const dataActorSelector = generateDataActorSelector({ dataActorOptions: { dataActorType: "entity", entity }, dataActorId });
    const dataActor = dataActorSelector(machineService.getSnapshot());
    return dataActor.ref;
};

export const generateDataForSavingEvent = ({ nextValue = null, colDef, object, onValueChange }) => {
    let valueToSave = nextValue;
    const expectedValue = nextValue;
    const { field, cellEditorParams = {} } = colDef;
    const { fieldToUpdate = null, propertyToSave = null } = cellEditorParams;
    if (propertyToSave) {
        if (Array.isArray(valueToSave)) {
            valueToSave = nextValue?.map((obj) => obj?.[propertyToSave] ?? obj);
        }
        valueToSave = valueToSave?.[propertyToSave] ?? valueToSave;
    }
    if (onValueChange) {
        const { object: updatedObj, field: updateField } = onValueChange(object);
        return {
            object: { ...object, ...updatedObj },
            fieldsBeingUpdated: { [field]: valueToSave, [updateField]: updatedObj[updateField] },
            expectedUpdateResult: { ...object, ...updatedObj }
        };
    }
    if (!fieldToUpdate) {
        return {
            object,
            field,
            value: valueToSave,
            expectedValue
        };
    }

    const { [field]: _fieldToNotUpdateValue, ...restOfObject } = object;
    const newObjectToUpdate = { ...restOfObject, [fieldToUpdate]: _fieldToNotUpdateValue };
    return {
        object: newObjectToUpdate,
        field: fieldToUpdate,
        value: valueToSave,
        expectedValue
    };
};

const sendUpdateDataEventToDataActor = ({ sender, objectToUpdate, field, value, expectedValue, optimisticUpdate = false }) => {
    const event = {
        type: "UPDATE_DATA",
        object: { ...objectToUpdate },
        fieldBeingUpdated: { [field]: value },
        optimisticUpdate
    };
    if (expectedValue) {
        event.expectedUpdateResult = { [field]: expectedValue };
    }
    sender(event);
};

export const relateBobj = ({
    bobj,
    targetBobj,
    targetBobjRelationshipAttribute,
    targetBobjRelationshipType,
    sendToTargetBobjEntityDataActor,
    optimisticUpdate
}) => {
    let nextTargetBobjRelatedValue;
    let nextTargetBobjRelatedExpectedValue;

    const targetBobjCurrentRelatedValue = targetBobj?.[targetBobjRelationshipAttribute];
    switch (targetBobjRelationshipType) {
        case "many":
            const mapOfCurrentRelatedValuesById = Array.isArray(targetBobjCurrentRelatedValue)
                ? targetBobjCurrentRelatedValue?.reduce((acc, nextRelatedBobOrId) => {
                    return {
                        ...acc,
                        [nextRelatedBobOrId?.id ?? nextRelatedBobOrId]: nextRelatedBobOrId
                    };
                }, {})
                : {};

            const idListOfCurrentRelatedValue = Object.keys(mapOfCurrentRelatedValuesById);

            const nextTargetBobRelatedValuesMapById = {
                ...mapOfCurrentRelatedValuesById,
                [bobj.id]: bobj
            };

            nextTargetBobjRelatedValue = Object.keys(nextTargetBobRelatedValuesMapById);
            nextTargetBobjRelatedExpectedValue = Object.values(nextTargetBobRelatedValuesMapById);
            if (!isEqual(idListOfCurrentRelatedValue, nextTargetBobjRelatedValue)) {
                const nextTargetBobj = {
                    ...targetBobj,
                    [targetBobjRelationshipAttribute]: nextTargetBobjRelatedValue
                };
                sendUpdateDataEventToDataActor({
                    sender: sendToTargetBobjEntityDataActor,
                    objectToUpdate: nextTargetBobj,
                    field: targetBobjRelationshipAttribute,
                    value: nextTargetBobjRelatedValue,
                    expectedValue: nextTargetBobjRelatedExpectedValue,
                    optimisticUpdate
                });
            }
            break;
        case "one":
            nextTargetBobjRelatedValue = bobj.id;
            nextTargetBobjRelatedExpectedValue = bobj;
            if (!isEqual(targetBobjCurrentRelatedValue, nextTargetBobjRelatedValue)) {
                const nextTargetBobj = {
                    ...targetBobj,
                    [targetBobjRelationshipAttribute]: nextTargetBobjRelatedValue
                };
                sendUpdateDataEventToDataActor({
                    sender: sendToTargetBobjEntityDataActor,
                    objectToUpdate: nextTargetBobj,
                    field: targetBobjRelationshipAttribute,
                    value: nextTargetBobjRelatedValue,
                    expectedValue: nextTargetBobjRelatedExpectedValue,
                    optimisticUpdate
                });
            }
            break;
        default:
            break;
    }
};

export const unrelateBobj = ({
    bobj,
    targetBobj,
    targetBobjRelationshipAttribute,
    targetBobjRelationshipType,
    sendToTargetBobjEntityDataActor,
    optimisticUpdate
}) => {
    const targetBobjCurrentRelatedValue = targetBobj?.[targetBobjRelationshipAttribute];
    let nextTargetBobjRelatedValue;
    switch (targetBobjRelationshipType) {
        case "many":
            let idListOfCurrentRelatedValue;
            if (Array.isArray(targetBobjCurrentRelatedValue)) {
                idListOfCurrentRelatedValue = targetBobjCurrentRelatedValue
                    .map((relatedBobjOrId) => relatedBobjOrId?.id ?? relatedBobjOrId);
            } else {
                idListOfCurrentRelatedValue = [];
            }
            nextTargetBobjRelatedValue = idListOfCurrentRelatedValue.filter((bobjId) => bobjId !== bobj.id);
            const uniqueNextTargetBobjRelatedValue = Array.from(new Set(nextTargetBobjRelatedValue));

            if (!isEqual(idListOfCurrentRelatedValue, uniqueNextTargetBobjRelatedValue)) {
                const nextTargetBobj = {
                    ...targetBobj,
                    [targetBobjRelationshipAttribute]: uniqueNextTargetBobjRelatedValue
                };
                sendUpdateDataEventToDataActor({
                    sender: sendToTargetBobjEntityDataActor,
                    objectToUpdate: nextTargetBobj,
                    field: targetBobjRelationshipAttribute,
                    value: uniqueNextTargetBobjRelatedValue,
                    optimisticUpdate
                });
            }
            break;
        case "one":
            if (targetBobjCurrentRelatedValue === bobj.id) {
                nextTargetBobjRelatedValue = null;
            } else {
                nextTargetBobjRelatedValue = targetBobjCurrentRelatedValue;
            }
            if (!isEqual(targetBobjCurrentRelatedValue, nextTargetBobjRelatedValue)) {
                const nextTargetBobj = {
                    ...targetBobj,
                    [targetBobjRelationshipAttribute]: nextTargetBobjRelatedValue
                };
                sendUpdateDataEventToDataActor({
                    sender: sendToTargetBobjEntityDataActor,
                    objectToUpdate: nextTargetBobj,
                    field: targetBobjRelationshipAttribute,
                    value: nextTargetBobjRelatedValue,
                    optimisticUpdate
                });
            }
            break;
        default:
            break;
    }
};

export const getBobjFromListUsingIdOrBobj = ({ bobjList = [], IdOrBobj }) => {
    if (typeof IdOrBobj === "string") {
        return bobjList.find((bob) => bob.id === IdOrBobj);
    }
    return IdOrBobj;
};

/**
 * Add values to the mapped attribute of the bobj
 * Useful to update the mapped attribute in bulk.
 *
 * For example: Add 2 products to a microsite
 * bobj - The microsite
 * mappedAttribute - the product attribute of the microsite bobj
 * valuesToAdd - The list of products to add to bobj[mappedAttribute]
 *
 * @param dataMachineService
 * @param bobj - Object with the mapped attribute
 * @param mappedAttribute - Mapped attribute name
 * @param valuesToAdd - List of values to add to bobj[mappedAttribute]
 */
export const addToListOfMappedBobjAttribute = ({
    dataMachineService,
    bobj,
    mappedAttribute,
    valuesToAdd,
    mappedBobjDataActorId,
    optimisticUpdate
}) => {
    const { entity } = bobj;
    const { send: sender } = getActorRefFromMachineService({ machineService: dataMachineService, entity, dataActorId: mappedBobjDataActorId });
    const currentValue = bobj?.[mappedAttribute]?.map((obj) => isObject(obj) ? obj.id : obj);
    let nextValue;
    if (Array.isArray(currentValue)) {
        nextValue = [...currentValue, ...valuesToAdd];
    } else {
        nextValue = [...valuesToAdd];
    }
    nextValue = Array.from(new Set(nextValue));
    sendUpdateDataEventToDataActor({
        sender: sender,
        objectToUpdate: bobj,
        field: mappedAttribute,
        value: nextValue,
        optimisticUpdate
    });
};

export const removeFromListOfMappedBobjAttribute = ({
    dataMachineService,
    bobj,
    mappedAttribute,
    valuesToRemove,
    mappedBobjDataActorId,
    optimisticUpdate
}) => {
    const { entity } = bobj;
    const { send: sender } = getActorRefFromMachineService({ machineService: dataMachineService, entity, dataActorId: mappedBobjDataActorId });
    const currentValue = bobj?.[mappedAttribute].map((obj) => isObject(obj) ? obj.id : obj);
    const nextValue = currentValue.filter((val) => !valuesToRemove.includes(val));
    sendUpdateDataEventToDataActor({
        sender: sender,
        objectToUpdate: bobj,
        field: mappedAttribute,
        value: nextValue,
        optimisticUpdate
    });
};

export const updateMappedBobj = ({
    bobj,
    dataMachineService,
    relationshipConfig,
    nextValue,
    currentValue,
    mappedBobjDataActorId,
    optimisticUpdate
}) => {
    const relatedBusinessObjects = getActorDataFromMachineService({ machineService: dataMachineService, entity: relationshipConfig?.relation, dataActorId: mappedBobjDataActorId });
    const relatedEntityDataActor = getActorRefFromMachineService({ machineService: dataMachineService, entity: relationshipConfig?.relation, dataActorId: mappedBobjDataActorId });
    const send = relatedEntityDataActor.send;
    const attributeMappedTo = relationshipConfig.mappedTo.split(".")[1];
    switch (relationshipConfig.relationshipType) {
        case "many":
            const valueBeingPersisted = Array.isArray(nextValue) ? nextValue : [];
            const valuesToUnrelate = currentValue && currentValue?.filter((previouslyRelatedValue) => {
                if (typeof previouslyRelatedValue === "string") {
                    return !valueBeingPersisted.includes(previouslyRelatedValue);
                }
                return !valueBeingPersisted.find((valToPersist) => valToPersist?.id === previouslyRelatedValue?.id);
            });

            valueBeingPersisted.map((IdOrBobj) => getBobjFromListUsingIdOrBobj({ bobjList: relatedBusinessObjects, IdOrBobj }))
                .forEach((targetBobj) => {
                    relateBobj({
                        bobj,
                        targetBobj,
                        targetBobjRelationshipAttribute: attributeMappedTo,
                        targetBobjRelationshipType: relationshipConfig.mappedToRelationshipType,
                        sendToTargetBobjEntityDataActor: send,
                        optimisticUpdate
                    });
                });
            valuesToUnrelate?.map((IdOrBobj) => getBobjFromListUsingIdOrBobj({ bobjList: relatedBusinessObjects, IdOrBobj }))
                .forEach((targetBobj) => {
                    unrelateBobj({
                        bobj,
                        targetBobj,
                        targetBobjRelationshipAttribute: attributeMappedTo,
                        targetBobjRelationshipType: relationshipConfig.mappedToRelationshipType,
                        sendToTargetBobjEntityDataActor: send,
                        optimisticUpdate
                    });
                });
            break;
        case "one": // The relationship type is one, so make the change to the related entity
            if (nextValue !== null) {
                const targetBobj = getBobjFromListUsingIdOrBobj({ bobjList: relatedBusinessObjects, IdOrBobj: nextValue });
                relateBobj({
                    bobj,
                    targetBobj,
                    targetBobjRelationshipAttribute: attributeMappedTo,
                    targetBobjRelationshipType: relationshipConfig.mappedToRelationshipType,
                    sendToTargetBobjEntityDataActor: send,
                    optimisticUpdate
                });
            }
            if (currentValue?.id !== nextValue) {
                unrelateBobj({
                    bobj,
                    targetBobj: currentValue,
                    targetBobjRelationshipAttribute: attributeMappedTo,
                    targetBobjRelationshipType: relationshipConfig.mappedToRelationshipType,
                    sendToTargetBobjEntityDataActor: send,
                    optimisticUpdate
                });
            }
            break;
        default:
            break;
    }
};

/**
 * Given a bobj to create
 * Update any mapped properties with either the set value or default value
 */
export const createMappedBobj = ({
    bobj,
    mappedAttributeList = [],
    dataMachineService,
    mappedBobjDataActorId,
    optimisticUpdate
}) => {
    mappedAttributeList.forEach((mappedAttribute) => {
        const { field: mappedAttributeField, cellEditorParams = {} } = mappedAttribute;
        const { relationshipConfig } = cellEditorParams;
        const bobjToCreateMappedAttributeValue = bobj?.[mappedAttributeField] ?? null;
        updateMappedBobj({
            bobj,
            dataMachineService,
            relationshipConfig,
            nextValue: bobjToCreateMappedAttributeValue,
            mappedBobjDataActorId,
            optimisticUpdate
        });
    });
};

export const FRESHNESS_PROCESSING_STRATEGY = {
    REPLACE: "REPLACE",
    MERGE: "MERGE",
    KEEP_TRANSIENT: "KEEP_TRANSIENT"
};

/**
 * Converts an attribute value path to the projection list required to load the value on the path
 *
 * @param attribute
 * @param initialEntitySchemaList
 * @param initialEntity
 * @param getEntityColumnsList
 * @throws Projection List Incomplete For Path
 * @returns {string[]}
 */
export const reduceNestedAttributeProjectionList = ({ attribute, initialEntitySchemaList, initialEntity, getEntityColumnsList }) => {
    const attributeSplit = attribute.split(".");

    const { projectionAttributeList } = attributeSplit.reduce(({ projectionAttributeList: currentProjectionAttributeList, entitySchemaList, currentEntity }, nextAttribute, attributeIndex) => {
        if (attributeSplit[attributeIndex + 1]) {
            const propertyName = attributeSplit[attributeIndex + 1];
            const attributeSchemaObj = findInList(entitySchemaList, { attribute: nextAttribute });
            if (!attributeSchemaObj) {
                throw new Error(`[Sweft] Projection List Incomplete For Path '${attribute}': Attribute '${nextAttribute}' is not in Entity '${currentEntity}' attribute list.`);
            }
            const { relation: nextEntity, colId } = attributeSchemaObj;
            if (!nextEntity) {
                throw new Error(`[Sweft] Projection List Incomplete For Path '${attribute}': Attribute '${nextAttribute}' of Entity '${currentEntity}' does not have a relation configured.`);
            }
            const nextProjectionAttributeList = [
                ...currentProjectionAttributeList,
                colId,
                `${nextEntity}.${propertyName}`
            ];
            const nextEntitySchemaList = getEntityColumnsList({ entity: nextEntity });
            if (!nextEntitySchemaList) {
                throw new Error(`[Sweft] Projection List Incomplete For Path '${attribute}': Related entity of Attribute '${colId}' does not have a schema attribute list.`);
            }
            return {
                projectionAttributeList: nextProjectionAttributeList,
                entitySchemaList: nextEntitySchemaList,
                currentEntity: nextEntity
            };
        }
        return { projectionAttributeList: currentProjectionAttributeList, entitySchemaList, currentEntity };
    }, { projectionAttributeList: [], entitySchemaList: initialEntitySchemaList, currentEntity: initialEntity });

    return Array.from(new Set(projectionAttributeList));
};
